// components/user/lead/calculator/DeviceInfoCard.jsx

import React from 'react';

const DeviceInfoCard = ({ 
  productData, 
  deviceAge, 
  selectedFaults, 
  selectedOptions,
  faultCategories,
  upgradeOptions,
  selectedUpgrades,
  ageRanges,
  isDarkMode 
}) => {
  const selectedCount = Object.values(selectedFaults || {}).filter(v => v === true).length;
  
  const getSelectedFaultDetails = () => {
    const selected = [];
    
    if (!faultCategories || !Array.isArray(faultCategories)) {
      return selected;
    }
    
    faultCategories.forEach(category => {
      if (category.faults && Array.isArray(category.faults)) {
        category.faults.forEach(fault => {
          // Check if fault is selected
          const isFaultSelected = selectedFaults && selectedFaults[fault.id] === true;
          
          if (isFaultSelected) {
            // Get selected option name if this fault has options
            let selectedOptionName = null;
            if (fault.options && fault.options.length > 0 && selectedOptions) {
              const optionId = selectedOptions[fault.id];
              if (optionId) {
                const option = fault.options.find(o => o.id === optionId);
                if (option) {
                  selectedOptionName = option.name;
                }
              }
            }
            
            selected.push({
              ...fault,
              categoryColor: category.selection_color || "#22c55e",
              categoryName: category.name,
              selectedOptionName: selectedOptionName,
              hasOptions: fault.options && fault.options.length > 0
            });
          }
        });
      }
    });
    return selected;
  };
  
  const selectedFaultDetails = getSelectedFaultDetails();

  // Get selected upgrade details
  const getSelectedUpgradeDetails = () => {
    const selected = [];
    if (!upgradeOptions || !Array.isArray(upgradeOptions)) {
      return selected;
    }
    
    upgradeOptions.forEach(option => {
      if (option.options && Array.isArray(option.options)) {
        option.options.forEach(opt => {
          if (selectedUpgrades && selectedUpgrades.includes(opt.id)) {
            selected.push({
              ...opt,
              categoryName: option.name
            });
          }
        });
      }
    });
    return selected;
  };
  
  const selectedUpgradeDetails = getSelectedUpgradeDetails();

  const getAgeLabel = () => {
    if (!deviceAge) return 'Not selected';
    
    if (ageRanges && Array.isArray(ageRanges) && ageRanges.length > 0) {
      const found = ageRanges.find(range => range.key === deviceAge);
      if (found) return found.label;
    }
    
    return deviceAge;
  };

  return (
    <div className={`sticky top-20 rounded-lg overflow-hidden ${isDarkMode ? "bg-gray-800" : "bg-white shadow-sm"}`}>
      <div className={`p-4 text-center border-b ${isDarkMode ? "border-gray-700" : "border-gray-100"}`}>
        {productData && (
          <>
            <img
              src={productData.product_image}
              alt={productData.product_name}
              className="object-contain mx-auto mb-3 w-28 h-28"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/placeholder-image.png';
              }}
            />
            <h3 className={`text-base font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
              {productData.product_name || 'Product'}
            </h3>
            <p className={`text-xs mt-1 font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              {productData.variation_name || 'Variation'}
            </p>
          </>
        )}
      </div>

      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
            Device Age
          </span>
          <span className={`text-xs font-bold ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
            {getAgeLabel()}
          </span>
        </div>
        
        {selectedUpgradeDetails.length > 0 && (
          <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
            <p className={`text-xs font-medium mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              Selected Specs
            </p>
            {selectedUpgradeDetails.map((upgrade) => (
              <div key={upgrade.id} className="flex items-center justify-between text-xs">
                <span className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
                  {upgrade.categoryName}
                </span>
                <span className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>
                  {upgrade.name}
                </span>
              </div>
            ))}
          </div>
        )}
        
        <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
          <span className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
            Issues Selected
          </span>
          <span className={`text-xs font-bold ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
            {selectedCount}
          </span>
        </div>
      </div>

      <div className={`h-px ${isDarkMode ? "bg-gray-700" : "bg-gray-100"}`}></div>

      <div className="p-4">
        {selectedCount === 0 ? (
          <div className="text-center">
            <p className={`text-[10px] font-semibold uppercase tracking-wide ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
              Estimated Value
            </p>
            <p className={`text-xs font-medium mt-2 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
              Select issues and click "Get Estimated Value" to see price
            </p>
          </div>
        ) : (
          <>
            <p className={`text-[10px] font-bold mb-2 uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              Selected Issues ({selectedCount})
            </p>
            <div className="space-y-2 overflow-y-auto max-h-40">
              {selectedFaultDetails.length > 0 ? (
                selectedFaultDetails.map((fault) => (
                  <div key={fault.id} className="flex items-start gap-2">
                    <div
                      className="flex-shrink-0 w-2 h-2 mt-1 rounded-full"
                      style={{ backgroundColor: fault.categoryColor || "#22c55e" }}
                    ></div>
                    <div className="flex-1 min-w-0">
                      <span className={`text-[11px] font-medium ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                        {fault.name || 'Unnamed fault'}
                      </span>
                      {fault.hasOptions && fault.selectedOptionName ? (
                        <span className={`ml-1 text-[10px] font-medium ${isDarkMode ? "text-green-400" : "text-green-600"}`}>
                          ({fault.selectedOptionName})
                        </span>
                      ) : fault.hasOptions && !fault.selectedOptionName ? (
                        <span className={`ml-1 text-[10px] font-medium ${isDarkMode ? "text-amber-400" : "text-amber-600"}`}>
                          (No option selected)
                        </span>
                      ) : null}
                    </div>
                  </div>
                ))
              ) : (
                <p className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                  No fault details available
                </p>
              )}
            </div>
            
            <div className={`mt-3 pt-3 border-t ${isDarkMode ? "border-gray-700" : "border-gray-100"}`}>
              <p className={`text-[10px] font-semibold uppercase tracking-wide ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                Estimated Value
              </p>
              <p className={`text-xs font-medium mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                Click "Get Estimated Value" to see price
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DeviceInfoCard;